import { useEffect, useState } from "react";
import axios from "axios";
import {
  Handshake, Quote, LayoutGrid, Image as ImageIcon, Images, Mail,
  Inbox, Settings as SettingsIcon, LogOut, ExternalLink, Newspaper,
  Users, FileText, UsersRound, ClipboardSignature, Calculator as CalculatorIcon,
  LayoutDashboard, Menu, X, Calendar as CalendarIcon,
} from "lucide-react";
import { API, getToken, setToken, authHeaders, formatApiErrorDetail } from "./api";
import { AField } from "./ui";
import { CrudSection } from "./CrudSection";
import { EnquiriesSection } from "./EnquiriesSection";
import { HeroSection, BannersSection, ContactSection } from "./ContentSections";
import { SettingsSection } from "./SettingsSection";
import { InsightsSection } from "./InsightsSection";
import { CustomersSection } from "./CustomersSection";
import { InvoicesSection } from "./InvoicesSection";
import { UsersSection } from "./UsersSection";
import { CalculatorSection } from "./CalculatorSection";
import { PartnersSection } from "./PartnersSection";
import { DashboardSection } from "./DashboardSection";
import { AppointmentsSection } from "./AppointmentsSection";
import { ICON_NAMES } from "../data/icons";
import { usePageMeta } from "../hooks/usePageMeta";

const PAGE_LINKS = [
  { value: "/", label: "Home" },
  { value: "/about", label: "About" },
  { value: "/investors", label: "Property Investors" },
  { value: "/hmo-services", label: "HMO Services" },
  { value: "/tenant-leasehold-advice", label: "Tenant & Leasehold Advice" },
  { value: "/how-we-work", label: "How We Work" },
  { value: "/stamp-duty-calculator", label: "Stamp Duty Calculator" },
  { value: "/insights", label: "Insights" },
  { value: "/faq", label: "FAQ" },
  { value: "/contact", label: "Contact" },
];

// Grouped navigation — mirrors a modern online-banking dashboard.
const NAV_GROUPS = [
  {
    label: null,
    items: [
      { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    ],
  },
  {
    label: "Clients",
    items: [
      { id: "enquiries", label: "Enquiries", icon: Inbox },
      { id: "customers", label: "Customers", icon: Users },
      { id: "appointments", label: "Appointments", icon: CalendarIcon },
    ],
  },
  {
    label: "Money",
    items: [
      { id: "invoices", label: "Invoices", icon: FileText },
      { id: "calculator", label: "Calculator", icon: CalculatorIcon },
    ],
  },
  {
    label: "Website",
    items: [
      { id: "partners", label: "Partners", icon: Handshake },
      { id: "testimonials", label: "Testimonials", icon: Quote },
      { id: "services", label: "Services", icon: LayoutGrid },
      { id: "hero", label: "Hero Content", icon: ImageIcon },
      { id: "banners", label: "Page Banners", icon: Images },
      { id: "contact", label: "Contact Details", icon: Mail },
      { id: "insights", label: "Insights", icon: Newspaper },
    ],
  },
  {
    label: "Account",
    items: [
      { id: "users", label: "Users", icon: UsersRound, adminOnly: true },
      { id: "settings", label: "Settings", icon: SettingsIcon, adminOnly: true },
    ],
  },
];

const ALL_ITEMS = NAV_GROUPS.flatMap((g) => g.items);

const AdminLogin = ({ onSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/auth/login`, { email, password });
      setToken(data.token);
      onSuccess();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-forest flex items-center justify-center px-6 grain relative">
      <form
        data-testid="admin-login-form"
        onSubmit={submit}
        className="relative z-[2] w-full max-w-md bg-cream p-8 sm:p-12 shadow-[0_30px_80px_rgba(0,0,0,0.4)]"
      >
        <img src="/logo.png" alt="Elin's Consultancy" className="h-20 w-auto mx-auto mb-8" />
        <h1 className="font-serif text-3xl font-medium text-forest text-center">Back Office</h1>
        <p className="text-[12px] text-sage text-center mt-2 mb-8">Sign in to manage your website</p>
        {error && (
          <p data-testid="admin-login-error" className="bg-red-50 border border-red-200 text-red-700 text-[13px] px-4 py-3 mb-5">{error}</p>
        )}
        <div className="space-y-5">
          <AField label="Email" testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
          <AField label="Password" testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
        </div>
        <button type="submit" data-testid="admin-login-button" disabled={busy} className="btn-gold w-full justify-center mt-8 disabled:opacity-60">
          {busy ? "Signing in..." : "Sign In"}
        </button>
      </form>
    </div>
  );
};

const NavItem = ({ item, active, onClick }) => (
  <button
    data-testid={`admin-nav-${item.id}`}
    onClick={onClick}
    className={`group w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-[13px] transition-colors ${
      active
        ? "bg-forest-soft text-gold-soft font-semibold"
        : "text-cream/70 hover:text-cream hover:bg-forest-soft/50 font-medium"
    }`}
  >
    <item.icon size={16} strokeWidth={1.7} className={active ? "text-gold" : "text-cream/50 group-hover:text-cream/80"} />
    <span className="truncate">{item.label}</span>
  </button>
);

const SidebarContent = ({ visibleGroups, section, setSection, logout, closeMobile }) => (
  <>
    <div className="px-6 pt-7 pb-6 border-b border-gold-soft/10 flex items-center justify-between">
      <div>
        <img src="/logo.png" alt="Elin's Consultancy" className="h-14 w-auto" />
        <p className="text-[9px] tracking-[0.32em] uppercase text-gold-soft mt-3">Back Office</p>
      </div>
      {closeMobile && (
        <button
          data-testid="admin-sidebar-close"
          onClick={closeMobile}
          className="lg:hidden p-2 text-cream/70 hover:text-cream"
          aria-label="Close menu"
        >
          <X size={20} />
        </button>
      )}
    </div>
    <nav data-lenis-prevent className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 py-5 space-y-6">
      {visibleGroups.map((g, gi) => (
        <div key={g.label || `group-${gi}`}>
          {g.label && (
            <p className="px-4 text-[10px] tracking-[0.24em] uppercase text-gold-soft/70 font-bold mb-2">
              {g.label}
            </p>
          )}
          <div className="space-y-1">
            {g.items.map((item) => (
              <NavItem
                key={item.id}
                item={item}
                active={section === item.id}
                onClick={() => {
                  setSection(item.id);
                  closeMobile?.();
                }}
              />
            ))}
          </div>
        </div>
      ))}

      {/* Account quick actions sit visually inside Account group but behave like links/buttons */}
      <div>
        <p className="px-4 text-[10px] tracking-[0.24em] uppercase text-gold-soft/70 font-bold mb-2">Session</p>
        <div className="space-y-1">
          <a
            href="/"
            data-testid="admin-view-site"
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-[13px] text-cream/70 hover:text-cream hover:bg-forest-soft/50 font-medium transition-colors"
          >
            <ExternalLink size={16} strokeWidth={1.7} className="text-cream/50" />
            View Site
          </a>
          <button
            data-testid="admin-logout-button"
            onClick={logout}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-[13px] text-cream/70 hover:text-cream hover:bg-forest-soft/50 font-medium transition-colors"
          >
            <LogOut size={16} strokeWidth={1.7} className="text-cream/50" />
            Sign Out
          </button>
        </div>
      </div>
    </nav>
  </>
);

export default function AdminApp() {
  usePageMeta("Back Office | Elin's Consultancy", "Admin area for Elin's Consultancy.");
  const [status, setStatus] = useState("checking");
  const [section, setSection] = useState("dashboard");
  const [focus, setFocus] = useState(null); // { focusId, source }
  const [me, setMe] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const fetchMe = () =>
    axios
      .get(`${API}/admin/auth/me`, authHeaders())
      .then(({ data }) => {
        setMe(data);
        setStatus("ready");
      })
      .catch(() => {
        setToken(null);
        setMe(null);
        setStatus("login");
      });

  useEffect(() => {
    if (!getToken()) {
      setStatus("login");
      return;
    }
    fetchMe();
  }, []);

  if (status === "checking") {
    return <div className="min-h-screen bg-cream-3 flex items-center justify-center text-sage text-sm">Loading...</div>;
  }
  if (status === "login") {
    return <AdminLogin onSuccess={() => { setStatus("checking"); fetchMe(); }} />;
  }

  const logout = () => {
    setToken(null);
    setMe(null);
    setStatus("login");
  };

  const isAdmin = me?.role === "admin";
  const visibleGroups = NAV_GROUPS
    .map((g) => ({ ...g, items: g.items.filter((i) => !i.adminOnly || isAdmin) }))
    .filter((g) => g.items.length > 0);
  const visibleIds = visibleGroups.flatMap((g) => g.items.map((i) => i.id));
  // If a staff user had an admin-only section selected, bounce them to Dashboard.
  const safeSection = visibleIds.includes(section) ? section : "dashboard";
  if (safeSection !== section) setSection(safeSection);

  const navigate = (nextSection, payload = null) => {
    setSection(nextSection);
    setFocus(payload);
    setMobileOpen(false);
  };

  const renderSection = () => {
    switch (safeSection) {
      case "dashboard":
        return <DashboardSection me={me} onNavigate={navigate} />;
      case "partners":
        return (
          <PartnersSection
            fields={[
              { key: "name", label: "Partner Name", type: "text" },
              { key: "website_url", label: "Website address", type: "text", hint: "Optional. e.g. https://jmw.co.uk — leave blank to show the logo without a link. Opens in a new tab. Clicks are counted in the Partner clicks panel above." },
              { key: "order", label: "Sort Order", type: "number" },
              { key: "logo_url", label: "Logo Image", type: "image", trim: true, hint: "Upload a PNG or SVG with a transparent background. Recommended at least 400px wide for crisp display on retina screens. Transparent padding is auto-trimmed so the logo fills the tile." },
            ]}
            emptyItem={{ name: "", logo_url: "", website_url: "", order: 0 }}
            itemTitle={(p) => p.name}
            itemSub={(p) => (p.website_url ? p.website_url : `Sort order: ${p.order ?? 0}`)}
            itemThumb={(p) => p.logo_url}
          />
        );
      case "testimonials":
        return (
          <CrudSection
            title="Testimonials"
            subtitle="Client quotes shown on the homepage."
            collection="testimonials"
            testidPrefix="testimonials"
            fields={[
              { key: "name", label: "Client Name", type: "text" },
              { key: "role", label: "Title / Company (optional)", type: "text" },
              { key: "quote", label: "Quote", type: "textarea" },
            ]}
            emptyItem={{ name: "", role: "", quote: "" }}
            itemTitle={(t) => t.name}
            itemSub={(t) => t.role || t.quote}
          />
        );
      case "services":
        return (
          <CrudSection
            title="Services"
            subtitle="The service cards shown on the homepage."
            collection="services"
            testidPrefix="services"
            fields={[
              { key: "num", label: "Number (e.g. 01)", type: "text" },
              { key: "order", label: "Sort Order", type: "number" },
              { key: "title", label: "Service Title", type: "text" },
              { key: "icon", label: "Icon", type: "select", options: ICON_NAMES },
              { key: "text", label: "Description", type: "textarea" },
              { key: "to", label: "Links To Page", type: "select", options: PAGE_LINKS },
            ]}
            emptyItem={{ num: "01", title: "", text: "", icon: "file-text", to: "/contact", order: 0 }}
            itemTitle={(s) => `${s.num} — ${s.title}`}
            itemSub={(s) => s.text}
          />
        );
      case "hero":
        return <HeroSection />;
      case "banners":
        return <BannersSection />;
      case "contact":
        return <ContactSection />;
      case "settings":
        return <SettingsSection />;
      case "insights":
        return <InsightsSection />;
      case "invoices":
        return <InvoicesSection focus={focus} />;
      case "calculator":
        return <CalculatorSection />;
      case "customers":
        return <CustomersSection focus={focus} />;
      case "appointments":
        return <AppointmentsSection onNavigate={navigate} />;
      case "users":
        return <UsersSection currentUser={me} />;
      case "enquiries":
        return <EnquiriesSection focus={focus} />;
      default:
        return <DashboardSection me={me} onNavigate={navigate} />;
    }
  };

  const currentLabel = ALL_ITEMS.find((i) => i.id === safeSection)?.label || "Back Office";

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-cream-3">
      {/* Desktop sidebar */}
      <aside data-testid="admin-sidebar" data-lenis-prevent className="hidden lg:flex fixed inset-y-0 left-0 w-64 bg-forest flex-col z-40 overscroll-contain">
        <SidebarContent
          visibleGroups={visibleGroups}
          section={safeSection}
          setSection={setSection}
          logout={logout}
        />
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden bg-forest sticky top-0 z-40">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gold-soft/15">
          <button
            data-testid="admin-mobile-menu-toggle"
            onClick={() => setMobileOpen(true)}
            className="p-2 -ml-2 text-cream/80 hover:text-cream"
            aria-label="Open menu"
          >
            <Menu size={22} />
          </button>
          <img src="/logo.png" alt="Elin's Consultancy" className="h-9 w-auto" />
          <a
            href="/"
            data-testid="admin-view-site-mobile"
            className="p-2 -mr-2 text-cream/70 hover:text-cream"
            aria-label="View site"
          >
            <ExternalLink size={18} />
          </a>
        </div>
        <div className="px-5 py-3 border-b border-gold-soft/10 bg-forest-deep/40">
          <p className="text-[10px] tracking-[0.24em] uppercase text-gold-soft/80 font-bold">
            {currentLabel}
          </p>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <button
            aria-label="Close menu"
            onClick={() => setMobileOpen(false)}
            className="absolute inset-0 bg-black/50"
          />
          <aside
            data-testid="admin-mobile-drawer"
            data-lenis-prevent
            className="relative w-72 max-w-[85vw] h-full bg-forest flex flex-col overscroll-contain"
          >
            <SidebarContent
              visibleGroups={visibleGroups}
              section={safeSection}
              setSection={setSection}
              logout={() => { setMobileOpen(false); logout(); }}
              closeMobile={() => setMobileOpen(false)}
            />
          </aside>
        </div>
      )}

      <main className="lg:ml-64 p-5 sm:p-8 lg:p-12 max-w-[1400px]">{renderSection()}</main>
    </div>
  );
}
